import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/lib/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ROLE_LABELS } from "@/lib/stages";
import EditUserDialog from "@/components/users/EditUserDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Trash, Power, PencilSimple } from "@phosphor-icons/react";

const ROLE_BADGE = {
  admin: { bg: "#FEE2E2", text: "#991B1B" },
  manager: { bg: "#FEF3C7", text: "#92400E" },
  builder: { bg: "#DBEAFE", text: "#1E40AF" },
};

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const toggleDisabled = async (u) => {
    const path = u.disabled ? `/users/${u.id}/enable` : `/users/${u.id}/disable`;
    await api.post(path);
    fetchUsers();
  };

  const deleteUser = (u) => setConfirmDelete(u);
  const doDeleteUser = async () => {
    const u = confirmDelete;
    setConfirmDelete(null);
    if (!u) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      fetchUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">Admin</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl mt-1">Team</h1>
          <p className="text-sm text-zinc-600 mt-1">{users.length} users · synced with the mobile app</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#C8102E] hover:bg-[#A00D25] h-11" data-testid="add-user-button">
              <Plus size={18} weight="bold" className="mr-1.5" /> Add user
            </Button>
          </DialogTrigger>
          <NewUserDialog onCreated={() => { setOpen(false); fetchUsers(); }} />
        </Dialog>
      </div>

      <Card className="border-zinc-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-zinc-500">Loading…</div>
        ) : (
          <div className="divide-y divide-zinc-200">
            <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 bg-zinc-50 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
              <div className="col-span-4">User</div>
              <div className="col-span-4">Email</div>
              <div className="col-span-2">Role</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1"></div>
            </div>
            {users.map((u) => {
              const isSelf = u.id === me?.id;
              return (
                <div key={u.id} className="px-5 py-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center" data-testid={`user-row-${u.email}`}>
                  <div className="md:col-span-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-zinc-200 flex items-center justify-center font-heading font-bold">
                      {u.name?.[0]}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{u.name}</div>
                      {u.must_change_password && (
                        <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">Pending password</div>
                      )}
                    </div>
                  </div>
                  <div className="md:col-span-4 text-sm font-mono text-zinc-700 truncate">{u.email}</div>
                  <div className="md:col-span-2">
                    <span
                      className="text-[10px] uppercase tracking-[0.2em] font-bold rounded-md px-2 py-1"
                      style={{
                        background: ROLE_BADGE[u.role]?.bg || "#E4E4E7",
                        color: ROLE_BADGE[u.role]?.text || "#3F3F46",
                      }}
                    >
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </div>
                  <div className="md:col-span-1 text-sm">
                    {u.disabled ? (
                      <span className="text-red-700 font-semibold text-xs">Disabled</span>
                    ) : (
                      <span className="text-emerald-700 font-semibold text-xs">Active</span>
                    )}
                  </div>
                  <div className="md:col-span-1 flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditing(u)}
                      title="Edit user"
                      data-testid={`edit-user-${u.email}`}
                    >
                      <PencilSimple size={16} weight="bold" className="text-zinc-600" />
                    </Button>
                    {!isSelf && (
                      <>
                        <Button variant="ghost" size="icon" onClick={() => toggleDisabled(u)} title={u.disabled ? "Enable" : "Disable"} data-testid={`toggle-user-${u.email}`}>
                          <Power size={16} weight="bold" className={u.disabled ? "text-zinc-400" : "text-emerald-700"} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteUser(u)} title="Delete" data-testid={`delete-user-${u.email}`}>
                          <Trash size={16} weight="bold" className="text-red-600" />
                        </Button>
                      </>
                    )}
                    {isSelf && <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">You</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <EditUserDialog
        open={!!editing}
        user={editing}
        isSelf={editing?.id === me?.id}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); fetchUsers(); }}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="delete-user-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmDelete?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the user. They will lose access immediately. Past activity (scans, history) stays attributed to their name.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDeleteUser}
              className="bg-[#C8102E] hover:bg-[#A00D25] text-white"
              data-testid="delete-user-ok"
            >
              Delete user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function NewUserDialog({ onCreated }) {
  const [form, setForm] = useState({ email: "", name: "", role: "builder", password: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await api.post("/users", form);
      onCreated();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="font-heading">Add team member</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Full name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="new-user-name" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Email</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="new-user-email" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Role</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(ROLE_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Temporary password</Label>
          <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="new-user-password" />
        </div>
        {error && <div className="text-sm bg-red-50 text-red-700 border border-red-200 rounded-md px-3 py-2">{error}</div>}
        <DialogFooter>
          <Button type="submit" disabled={saving} className="bg-[#C8102E] hover:bg-[#A00D25]" data-testid="submit-new-user">
            {saving ? "Saving…" : "Create user"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
